import {AutogenLayoutLoadMatch} from "./autogen/AutogenLayoutLoadMatch";
import {Alignment, ModelRow} from "@mcleod/core";
import {CommonDialogs, CommonTooltips} from "@mcleod/common";
import {
    ChangeEvent, ClickEvent,
    DataSourceAction,
    DataSourceExecutionEvent,
    DataSourceMode,
    Label, Layout, Panel,
    SlideoutDecorator, SlideoutDecoratorProps
} from "@mcleod/components";
import {ExpandableFilterController} from "@mcleod/general";
import {RowOrderPlanningProfile} from "./models/ModelOrderPlanningProfile";
import { OrderPlanningOrders } from "./planning/opsmgmt/OrderPlanningOrders";
import { RowDriverOnlyFields } from "../../dispatch/asset/src/models/autogen/AutogenModelDriverOnly";
import { LoadMatchToolPanel } from "./planning/opsmgmt/LoadMatchToolPanel";

export type LoadMatchProps = {
    driverData: Partial<RowDriverOnlyFields>;
    tractorId?: string;
    trailerId?: string;
    equipmentGroupId: string;
    profileRow: RowOrderPlanningProfile;
    movementId?: string;
    orderId?: string;
}

export class LoadMatch extends AutogenLayoutLoadMatch {

    private driverData: Partial<RowDriverOnlyFields>;
    private profileRow: RowOrderPlanningProfile;
    private movementId: string;
    private orderId: string;
    private expandableFilter: ExpandableFilterController = new ExpandableFilterController();
    public tractorId?: string;
    public equipmentGroupId: string;

    public static openSlideout(props: LoadMatchProps, slideoutProps?: Partial<SlideoutDecoratorProps>) {
        const layout = Layout.getLayout("lme/dispatch/LoadMatch",  {...props, minWidth: 100}) as LoadMatch;
        SlideoutDecorator.decorateAndSlideIn(layout, {
            fillRow: true,
            height: '93%',
            title: `LoadMatch`,
            displayOverlay: true,
            overlayProps: { greyedBackground: true },
            transitionOptions: {direction: Alignment.BOTTOM},
            layoutLoadListeners: [(event) => {
                const layout = event.target as LoadMatch;
                layout.hideComponents(layout.layoutMatchingLoads);
                layout.hideComponents(layout.layoutNonMatchingLoads);
                layout.attemptSearch(true);
            }],
            ...slideoutProps
        });
    }

    onLoad(): void | Promise<void> {
        this.expandableFilter.initialize(this.panelFilters, this.panelFilterParams);
        this.expandableFilter.toggle();
    }

    hideComponents(ordersLayout: OrderPlanningOrders) {
        ordersLayout.layoutPlanningTitle.visible = false;
        ordersLayout.layoutOPAvailTools.visible = false;
        ordersLayout.panelAvailFilters.visible = false;
        ordersLayout.textboxDateRange.visible = false;
    }

    async attemptSearch(loading=false) {
        if (this.driverData?.id == null) {
            return;
        }
        this.layoutMatchingLoads.tableAvailableMoves.busy = true;
        this.layoutNonMatchingLoads.tableAvailableMoves.busy = true;
        this.labelLastUpdateRetrieved.style.fontStyle = "italic";
        CommonTooltips.setTooltipContentLayout(
            this.labelPreassignedValue,
            null,
            "lme/dispatch/planning/PreassignOrderQuickInfoTable",
            {width: 800},
            {"equipment_type": "D", "equipment_id": this.driverData?.id}
        );
        this.labelPreassignedValue.tooltipDynamicOptions = { position: Alignment.LEFT };
        this.layoutMatchingLoads.sourceAvailableMove.orderBy = null;
        this.layoutNonMatchingLoads.sourceAvailableMove.orderBy = null;
        const filter = {...this.mainDataSource.getSearchFilter().data,
            'driver_id': this.driverData?.id,
            'movement_id': this.movementId,
            'order_id': this.orderId,
            'profile_row': this.profileRow,
            'initial_search': loading
        };
        await this.mainDataSource.search(filter);

        const matchingLoads:ModelRow[] = [];
        const nonMatchingLoads:ModelRow[] = [];
        this.mainDataSource.data.forEach((data:ModelRow) => {
            if (data.getBoolean("matching")) {
                matchingLoads.push(data);
            } else {
                nonMatchingLoads.push(data);
            }
        })
        this.layoutMatchingLoads.sourceAvailableMove.clear();
        this.layoutNonMatchingLoads.sourceAvailableMove.clear();
        if (matchingLoads.length > 0) {
            this.layoutMatchingLoads.sourceAvailableMove.setRowsAndMode(DataSourceMode.NONE, matchingLoads);
        }
        if (nonMatchingLoads.length > 0) {
            this.layoutNonMatchingLoads.sourceAvailableMove.setRowsAndMode(DataSourceMode.NONE, nonMatchingLoads);
        }

        const changeListener = (event: ChangeEvent) => {
            if (event.newValue == null || event.newValue === "=") {
                this.mainDataSource.searchRow.set("driver_priority_two_value", null);
                this.mainDataSource.searchRow.set("driver_priority_two", null);
                this.panelFilterParams.displayData(this.mainDataSource.searchRow, null, 0);
            }
            this.textboxPriorityTwoValue.enabled = event.newValue !== null && event.newValue !== "=";
        }
        this.textboxPriorityTwo.removeChangeListener(changeListener); // prevent duplicate listeners
        this.textboxPriorityTwo.addChangeListener(changeListener);

        this.layoutMatchingLoads.tableAvailableMoves.busy = false;
        this.layoutNonMatchingLoads.tableAvailableMoves.busy = false;
    }

    private afterSearch(event: DataSourceExecutionEvent) {
        if (event.getAction() != DataSourceAction.SEARCH) return;
        const summary = event.dataSource.summaryData;

        this.panelHeader.displayData(summary, null, 0);
        this.displayFilterData(summary);
        this.setupLoadMatchTools(summary);

        this.mainDataSource.searchRow.clear();
        this.mainDataSource.searchRow.setValues(summary.data);
        this.panelHeader.displayData(this.mainDataSource.searchRow, null, 0);
        this.displayFilterData(this.mainDataSource.searchRow);
    }

    private setupLoadMatchTools(summary: ModelRow) {
        const driverRemoteAccess = summary.getBoolean("driver_remote_access");
        const initializeLoadMatchToolPanel = (layout: LoadMatchToolPanel) => {
            layout.initialize(
                this.driverData,
                driverRemoteAccess,
                this.equipmentGroupId,
                this.tractorId,
            )
        }
        initializeLoadMatchToolPanel(this.layoutMatchingLoads.layoutLMTools);
        initializeLoadMatchToolPanel(this.layoutNonMatchingLoads.layoutLMTools);
    }

    private displayFilterData(summary: ModelRow) {
        const usePreassignDest = summary.getBoolean("profile_use_preassign_dest");
        this.insertLabel(usePreassignDest && summary.getNumber("driver_preassigned", 0) > 0
            ? this.panelFinalPreassignDestination : this.panelFinalStop);
        this.panelFilterParams.displayData(summary, null, 0);
        this.textboxPriorityTwoValue.enabled = summary.getString("driver_priority_two", null) != null;
    }

    insertLabel(panel: Panel) {
        const id = "labelResultsBasedOn";
        if (panel.components[0].id === id)
            return;

        const labelPrefix = new Label({
            id,
            text: "LoadMatch Results Based On",
            fontBold: true,
            fontSize: "small",
            color: "warning.light",
            style: {fontStyle: "italic"}
        });
        panel.insert(labelPrefix, 0);
    }

    buttonSearchOnClick(event: ClickEvent) {
        if (!this.textboxPriorityOneValue.validateSimple()) {
            CommonDialogs.showError("Priority One Value is required");
            return;
        }
        if (this.mainDataSource.searchRow.get("driver_priority_one") === this.mainDataSource.searchRow.get("driver_priority_two")) {
            CommonDialogs.showError("Priority choice one and priority choice two can not be the same.");
            return;
        }
        this.attemptSearch()
    }

}
